'use client';

import React, { use, useCallback, useEffect, useState } from 'react';

import { logger } from '@/lib/logger.client';

import Icon from './Icon';
import Img from './Image';

export interface Line {
  id?: string;
  name?: string;
  status?: string;
  description?: string;
  picture?: { url?: string };
  gallery?: { url?: string }[];
}

export type SearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lines?: Line[];
  onSearch: (query: string, selectedLine?: Line) => void;
  className?: string;
};

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose, lines = [], onSearch, className = '' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLine, setSelectedLine] = useState<Line | undefined>(undefined);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedLine(undefined);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleSearch = useCallback(() => {
    onSearch(searchQuery, selectedLine);
    onClose();
  }, [searchQuery, selectedLine, onSearch, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const handleLineSelect = (line: Line) => {
    setSelectedLine(selectedLine?.id === line.id ? undefined : line);
  };

  useEffect(() => {
    logger.debug({ lines }, 'SearchModal - Líneas disponibles');
  }, [lines]);

  if (!isOpen) return null;

  return (
    <div className={`animated-fade-in duration-300 fixed inset-0 z-50 flex items-start justify-center bg-white/30 bg-opacity-20 backdrop-blur-sm ${className}`}>
      {/* Modal Container */}
      <div className="relative w-full max-w-4xl mx-4 mt-8 sm:mt-16 bg-white rounded-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold px-2 text-gray-900">Buscar productos</h2>
          <button onClick={onClose} className="h-10 w-10 cursor-pointer text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Cerrar modal de búsqueda">
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Search Form */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="¿Qué estás buscando?"
                className="w-full px-4 py-3 pl-12 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Icon name="search" className="h-5 w-5 text-gray-400" />
              </div>
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery('')} className="absolute cursor-pointer inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600">
                  <Icon name="close" className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Search Button */}
            <button type="submit" className="mt-4 cursor-pointer w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors font-medium">
              Buscar productos
            </button>
          </form>

          {/* Product Lines Selector */}
          {lines.length > 0 && (
            <div>
              <h3 className="text-base font-medium text-gray-900 mb-4">
                {selectedLine && `Buscar en línea específica - ${selectedLine.name}`}
                {!selectedLine && 'Elige una línea para filtrar tu búsqueda (opcional)'}
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {lines.map((line, index) => {
                  const isSelected = selectedLine?.id === line.id;
                  return (
                    <button
                      key={line.id ?? `line-${index}`}
                      type="button"
                      onClick={() => handleLineSelect(line)}
                      className={`
                                                relative p-4 rounded-lg border-2 transition-all duration-200 text-center cursor-pointer
                                                ${isSelected ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'}
                                            `}
                    >
                      {/* Line Image Placeholder */}
                      <div
                        className={`
                                                            w-full h-16 mb-2 rounded-md flex items-center justify-center
                                                            ${isSelected ? 'bg-primary-100' : 'bg-gray-100'}
                                                        `}
                      >
                        {line.picture ? (
                          <Img src={line.picture.url as string} alt={line.name || 'Line Image'} className="w-full h-full object-cover rounded-md" />
                        ) : (
                          <div
                            className={`
                                                                    text-2xl font-bold
                                                                    ${isSelected ? 'text-primary-600' : 'text-gray-400'}
                                                                `}
                          >
                            {line.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>

                      {/* Line Name */}
                      <p
                        className={`
                                                            text-xs font-medium truncate
                                                            ${isSelected ? 'text-primary-700' : 'text-gray-600'}
                                                        `}
                      >
                        {line.name}
                      </p>

                      {/* Selected Indicator */}
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Clear Line Selection */}
              {selectedLine && (
                <button type="button" onClick={() => setSelectedLine(undefined)} className="mt-4 text-sm text-gray-600 hover:text-gray-800 underline cursor-pointer">
                  Buscar en todas las líneas
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer with shortcuts */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex- items-center space-x-4 hidden">
              <span>
                <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">Enter</kbd> para buscar
              </span>
              <span>
                <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">Esc</kbd> para cerrar
              </span>
            </div>
            {selectedLine && <span className="text-primary-600 font-medium">Buscando en: {selectedLine.name}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
